import { MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cancelClassOptIn, createClassOptIn, createRankingSelection } from "../utils/blockUtils";
import { canCancelClass } from "../../../../../../../utils/classUtils";
import { IClasses } from "../../../../../../../models/classes";
import styles from "./index.module.scss";
import ActionsButton from "../ActionsButton";
import TaskWrap from "../TaskWrap";
import { getSelectetdClass, getTaskInfo, isOption } from "./utils/utils";

interface SelectedClassId {
    userSelectionId: string;
    classId: string;
}

interface IBlockSession {
    classes: IClasses;
    type: string;
    userSelection: any;
    task: string;
    eventId: string;
    isTaskCompleted: boolean;
    allowSelect?: AllowSelectionAll;
    isProgramTeam: boolean;
    language?: string;
    getSelectedClassIds: () => SelectedClassId[];
    isTaskSelectingInProgress: boolean;
    setIsTaskSelectingInProgress: (value: boolean) => void;
    allUserSelections?: { [taskId: string]: any[] };
}

interface AllowSelectionAll {
    allowSelectAll: boolean;
    minSelect: number;
    maxSelect: number;
}

const BlockSession = ({
    classes,
    type,
    userSelection,
    task,
    eventId,
    isTaskCompleted,
    allowSelect,
    isProgramTeam,
    language,
    getSelectedClassIds,
    isTaskSelectingInProgress,
    setIsTaskSelectingInProgress,
    allUserSelections,
}: IBlockSession) => {
    const dispatch = useDispatch();
    const { me } = useSelector((state: any) => state.auth);
    const [blockLoader, setBlockLoader] = useState<boolean>(false);
    const [isSelectingInProgress, setIsSelectingInProgress] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const userSelectionRecord = userSelection && userSelection.records;
    const countSelectedTask = userSelectionRecord && userSelectionRecord.length;
    const isNotEnglish = language !== "English";
    const isClassFull = type === "Class Opt-in" ? classes.Participants_Cap_Remaining__c <= 0 : false;
    const SelectionRecord = getSelectetdClass(classes, userSelectionRecord);
    const isFirstOptionSelected = isOption(classes.HN_Session_title__c, userSelectionRecord, "Selected_Option__c");
    const isSecondOptionSelected = isOption(classes.HN_Session_title__c, userSelectionRecord, "Second_Option__c");
    const hasFirstOptionSelected = Boolean(userSelectionRecord?.[0].Selected_Option__c);
    const hasSecondOptionSelected = Boolean(userSelectionRecord?.[0].Second_Option__c);
    const isSelected = Boolean(SelectionRecord);
    
    const canCancelCurrentClass = isSelected ? canCancelClass(classes.Id, { Classes__r: [classes] }, allUserSelections) : true;
    
    const isActionButtonDisabled = isTaskCompleted || (isClassFull && !isSelected) || isProgramTeam || (isSelected && !canCancelCurrentClass);

    const updateRankingSelection = async (changeOrder = false) => {
        setError(null);
        const selectionOptions = {
            selectedOption: userSelectionRecord?.[0].Selected_Option__c || "",
            secondOption: userSelectionRecord?.[0].Second_Option__c || "",
        };

        if (!isSelected) {
            if (!changeOrder) {
                if (!countSelectedTask) selectionOptions.selectedOption = classes.HN_Session_title__c;
                else if (hasFirstOptionSelected) selectionOptions.secondOption = classes.HN_Session_title__c;
                else if (hasSecondOptionSelected) selectionOptions.selectedOption = classes.HN_Session_title__c;
            }
        }
        else {
            if (changeOrder) {
                if (hasFirstOptionSelected && !hasSecondOptionSelected) {
                    selectionOptions.secondOption = userSelectionRecord[0].Selected_Option__c;
                    selectionOptions.selectedOption = "";
                }
                else if (hasSecondOptionSelected && !hasFirstOptionSelected) {
                    selectionOptions.selectedOption = userSelectionRecord[0].Second_Option__c;
                    selectionOptions.secondOption = "";
                }
                else if (hasFirstOptionSelected && hasSecondOptionSelected) {
                    [selectionOptions.selectedOption, selectionOptions.secondOption] =
                        [selectionOptions.secondOption, selectionOptions.selectedOption];
                }
            }
            else {
                if (isFirstOptionSelected) {
                    selectionOptions.secondOption = "";
                    selectionOptions.selectedOption = userSelectionRecord[0].Second_Option__c || "";
                }
                else if (isSecondOptionSelected) {
                    selectionOptions.secondOption = "";
                }
            }
        }

        try {
            await createRankingSelection(
                task,
                me,
                eventId,
                selectionOptions,
                dispatch,
                setBlockLoader
            );
        } catch (err) {
            setError(err.message || "Failed to update ranking selection");
            throw err;
        }
    }

    const handleClassOptin = async () => {
        setError(null);
        try {
            if (isSelected) {
                await cancelClassOptIn(classes.Id, SelectionRecord.Id, me, eventId, dispatch, setBlockLoader, true);
            } else {
                await createClassOptIn(
                    classes.HN_Session_title__c,
                    classes.Id,
                    task,
                    me,
                    eventId,
                    getSelectedClassIds(),
                    dispatch,
                    allowSelect,
                    countSelectedTask,
                    userSelectionRecord,
                    setBlockLoader
                );
            }
        } catch (err) {
            setBlockLoader(false);
            if (err?.response?.status !== 409) {
                setError(err.message || "Failed to process class opt-in");
            }
            throw err;
        }
    };

    const isTaskSelectingInBackground = isTaskSelectingInProgress && !blockLoader;

    const handleActionButtonClick = async () => {
        if (!isActionButtonDisabled && !isSelectingInProgress && !isTaskSelectingInBackground) {
            setIsSelectingInProgress(true);
            setIsTaskSelectingInProgress(true);
            try {
                if (type === "Class Opt-in") {
                    await handleClassOptin();
                } else {
                    await updateRankingSelection();
                }
            } catch (err) {
                console.error("Action failed:", err);
            } finally {
                setIsSelectingInProgress(false);
                setIsTaskSelectingInProgress(false);
            }
        }
    };

    const handleChangeOrderClick = (event: MouseEvent<HTMLButtonElement>, order: number) => {
        event.stopPropagation();
        if (isActionButtonDisabled || (order === 1 && isFirstOptionSelected) || (order === 2 && isSecondOptionSelected)) return;
        updateRankingSelection(true);
    };


    return (
        classes && (
            <div 
            className={`${styles.container} ${isSelectingInProgress ? styles.disabled : ""} ${isSelected && !canCancelCurrentClass ? styles.cancellationDisabled : ""}`} 
            onClick={handleActionButtonClick}
            title={isSelected && !canCancelCurrentClass ? "Cannot cancel: This selection is required by other dependencies" : ""}
            style={
                isTaskSelectingInBackground ? { pointerEvents: "none", cursor: "not-allowed", opacity: 0.5 } : {}
            }>
                <ActionsButton
                    isProgramTeam={isProgramTeam}
                    isLoading={blockLoader}
                    isTaskCompleted={isTaskCompleted}
                    isClassFull={isClassFull}
                    isSelected={isSelected}
                    isCancellationDisabled={isSelected && !canCancelCurrentClass}
                />
                <TaskWrap
                    title={classes.HN_Session_title__c}
                    description={classes.HN_Session_desc__c}
                    isLoading={blockLoader}
                    isSelected={isSelected}
                    isClassFull={isClassFull}
                    isTaskCompleted={isTaskCompleted || isProgramTeam}
                    isNotEnglish={isNotEnglish}
                    taskInfo={getTaskInfo(classes, language, type)}
                    language={language}
                    changeOrder={handleChangeOrderClick}
                    error={error}
                    isCancellationDisabled={isSelected && !canCancelCurrentClass}
                    {...(type === "Class Ranking" && isSelected && {
                        isClassRanking: true,
                        isFirstOption: isFirstOptionSelected,
                        isSecondOption: isSecondOptionSelected,
                        hasFirstOptionSelected: hasFirstOptionSelected,
                        hasSecondOptionSelected: hasSecondOptionSelected
                    })}
                />
            </div>
        )
    );
};

export default BlockSession;